// ============================================================================
// PERFORMANCE VIEW — Stage 7 Operational Analytics v1
//
// Role access: ops_manager, tenant_admin
//
// Data: getDailyRollups(tenantId, buildDateRange(selectedDays)) — up to 30 reads.
// Invariant: Zero collection scans. Zero PHI reads.
//
// Layout:
//   1. Period selector (7d / 30d)
//   2. Gate Metrics summary (from existing counts.* + value.* fields)
//   3. Workflow Flow summary (flow.* + rework.*)
//   4. Daily breakdown table
// ============================================================================

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../auth/AuthContext.jsx';
import {
  getDailyRollups, buildDateRange, safeNum, sumField,
} from '../../core/analyticsApi.js';
import {
  TrendingUp, RefreshCw, AlertTriangle, ChevronRight,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function shortDate(dateId) {
  // '2026-03-05' → 'Mar 5'
  const d = new Date(dateId + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function pct(num, denom) {
  if (!denom) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, colorTw = 'text-white' }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/40 rounded-2xl p-4">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${colorTw}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function FunnelStep({ label, value, colorTw }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/40 min-w-[72px] text-center`}>
        <p className={`text-sm font-bold ${colorTw}`}>{value}</p>
        <p className="text-xs text-slate-500 leading-tight">{label}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
    </div>
  );
}

function SkeletonBlock({ h = 'h-28' }) {
  return (
    <div className={`bg-slate-800/30 border border-slate-700/20 rounded-2xl ${h} animate-pulse`} />
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

const PERIODS = [
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
];

export default function PerformanceView() {
  const { user } = useContext(AuthContext);
  const tenantId = user?.tenantId;

  const [period, setPeriod]       = useState(7);
  const [rollups, setRollups]     = useState(null);
  const [error, setError]         = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const dateIds = useMemo(() => buildDateRange(period), [period]);

  const load = async () => {
    // tenantId guaranteed by AnalyticsGuard (Stage 9)
    setRollups(null);
    try {
      const data = await getDailyRollups(tenantId, dateIds);
      setRollups(data);
      setError(null);
    } catch (err) {
      console.error('[PerformanceView] Failed to load rollups:', err);
      setError('Failed to load analytics data. Please try again.');
    }
  };

  useEffect(() => { load(); }, [tenantId, period]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  // ── Aggregated totals ────────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!rollups) return null;
    return {
      // Gate
      gate_total:     sumField(rollups, 'counts', 'total'),
      gate_not_yet:   sumField(rollups, 'counts', 'not_yet'),
      gate_urgent:    sumField(rollups, 'counts', 'urgent') + sumField(rollups, 'counts', 'urgent_not_yet'),
      gate_override:  sumField(rollups, 'counts', 'override'),
      minutes_saved:  sumField(rollups, 'value', 'reclaimedMinutes'),
      // Flow
      created:           sumField(rollups, 'flow', 'created'),
      needs_info:        sumField(rollups, 'flow', 'needsInfo'),
      ready_to_schedule: sumField(rollups, 'flow', 'readyToSchedule'),
      scheduled:         sumField(rollups, 'flow', 'scheduled'),
      seen:              sumField(rollups, 'flow', 'seen'),
      closed_loop:       sumField(rollups, 'flow', 'closedLoop'),
      // Rework
      rework_requests:   sumField(rollups, 'rework', 'requestsCreated'),
      rework_resolved:   sumField(rollups, 'rework', 'resolved'),
      rework_touches:    sumField(rollups, 'rework', 'touchesLogged'),
    };
  }, [rollups]);

  const loading = rollups === null;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Performance</h1>
            <p className="text-xs text-slate-400">Gate + workflow metrics</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Period selector */}
          <div className="flex rounded-xl bg-slate-800/60 border border-slate-700/40 p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.days}
                onClick={() => setPeriod(p.days)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  period === p.days
                    ? 'bg-blue-600/40 text-blue-300 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60
                       text-slate-400 hover:text-white text-xs font-medium disabled:opacity-50 transition-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30
                        rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ── Gate Metrics ──────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Gate Metrics</h2>
        {loading ? (
          <SkeletonBlock h="h-28" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <SummaryCard
              label="Total Referrals"
              value={totals.gate_total}
              colorTw="text-slate-200"
            />
            <SummaryCard
              label="Intercepted"
              value={totals.gate_not_yet}
              sub={pct(totals.gate_not_yet, totals.gate_total) + ' intercept rate'}
              colorTw="text-amber-400"
            />
            <SummaryCard
              label="Pass-through"
              value={totals.gate_total - totals.gate_not_yet}
              colorTw="text-slate-300"
            />
            <SummaryCard
              label="Urgent Flags"
              value={totals.gate_urgent}
              colorTw="text-red-400"
            />
            <SummaryCard
              label="Minutes Saved"
              value={totals.minutes_saved}
              colorTw="text-cyan-400"
            />
          </div>
        )}
      </section>

      {/* ── Workflow Flow ─────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Workflow Flow</h2>
        {loading ? (
          <SkeletonBlock h="h-36" />
        ) : (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-5 space-y-5">
            {/* Funnel row */}
            <div className="flex items-center flex-wrap gap-0">
              <FunnelStep label="Created"    value={totals.created}           colorTw="text-blue-400" />
              <FunnelStep label="Needs Info" value={totals.needs_info}        colorTw="text-amber-400" />
              <FunnelStep label="Ready"      value={totals.ready_to_schedule} colorTw="text-indigo-400" />
              <FunnelStep label="Scheduled"  value={totals.scheduled}         colorTw="text-purple-400" />
              <FunnelStep label="Seen"       value={totals.seen}              colorTw="text-teal-400" />
              {/* Last step — no trailing arrow */}
              <div className="px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 min-w-[72px] text-center">
                <p className="text-sm font-bold text-emerald-400">{totals.closed_loop}</p>
                <p className="text-xs text-slate-500 leading-tight">Closed Loop</p>
              </div>
            </div>

            {/* Rework stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-slate-700/40">
              <SummaryCard
                label="Rework Requests"
                value={totals.rework_requests}
                sub={`Rework rate: ${pct(totals.rework_requests, totals.created)}`}
                colorTw="text-rose-400"
              />
              <SummaryCard
                label="Rework Resolved"
                value={totals.rework_resolved}
                sub={`Resolution rate: ${pct(totals.rework_resolved, totals.rework_requests)}`}
                colorTw="text-emerald-400"
              />
              <SummaryCard
                label="Outreach Touches"
                value={totals.rework_touches}
                colorTw="text-slate-300"
              />
              <SummaryCard
                label="Completion Rate"
                value={pct(totals.closed_loop, totals.created)}
                sub="closed / created"
                colorTw="text-cyan-400"
              />
            </div>
          </div>
        )}
      </section>

      {/* ── Daily Breakdown Table ─────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Daily Breakdown</h2>
        {loading ? (
          <SkeletonBlock h="h-48" />
        ) : (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/60">
                    {['Date', 'Created', 'Needs Info', 'Scheduled', 'Seen', 'Closed', 'Rework'].map((h) => (
                      <th key={h} className="px-3 py-2.5 text-left font-medium text-slate-400 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Most recent first */}
                  {[...rollups].reverse().map((r) => (
                    <tr
                      key={r.dateId}
                      className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap font-medium">
                        {shortDate(r.dateId)}
                      </td>
                      <td className="px-3 py-2 text-blue-400">{safeNum(r, 'flow', 'created') || '—'}</td>
                      <td className="px-3 py-2 text-amber-400">{safeNum(r, 'flow', 'needsInfo') || '—'}</td>
                      <td className="px-3 py-2 text-purple-400">{safeNum(r, 'flow', 'scheduled') || '—'}</td>
                      <td className="px-3 py-2 text-teal-400">{safeNum(r, 'flow', 'seen') || '—'}</td>
                      <td className="px-3 py-2 text-emerald-400">{safeNum(r, 'flow', 'closedLoop') || '—'}</td>
                      <td className="px-3 py-2 text-rose-400">{safeNum(r, 'rework', 'requestsCreated') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <p className="text-xs text-slate-600 text-center">
        Data sourced from pre-aggregated daily rollups · Updates within seconds of each action
      </p>
    </div>
  );
}
